/** Player options saved in this browser: the colour palette and the rumble strength. */
export interface PaletteDef { name: string; colors: [string, string, string, string] } // lightest ... darkest of the original look
export const PALETTES: PaletteDef[] = [
  { name: "CLASSIC GREEN", colors: ["#e0f8d0", "#88c070", "#346856", "#081820"] },
  { name: "POCKET GREY", colors: ["#d8d8d0", "#a0a098", "#585850", "#101010"] },
  { name: "AMBER", colors: ["#ffe9a8", "#e0a030", "#8a4a10", "#2a1204"] },
  { name: "ICE BLUE", colors: ["#e0f0ff", "#88b8e8", "#3860a0", "#081830"] },
  { name: "SUNSET", colors: ["#fff0d0", "#f0a060", "#a04070", "#301040"] },
  { name: "HI-CONTRAST", colors: ["#ffffff", "#b4b4b4", "#505050", "#000000"] },
  { name: "NIGHT RED", colors: ["#1c0000", "#5c0808", "#b81818", "#ff5050"] }, // inverted: dark screen, bright drawing
];
export const RUMBLE_LEVELS = [
  { name: "OFF", k: 0 },
  { name: "LOW", k: 0.5 },
  { name: "NORMAL", k: 1 },
  { name: "HIGH", k: 1.8 },
];

export const VOLUME_STEPS = 10; // each volume setting is 0..10 (percent / 10)
export type VolumeKind = "music" | "car" | "skid";
/** Touch controls while racing: the pad and buttons, the whole screen, or a floating steering stick under the left thumb with the buttons kept on the right (left half steers, right thumb gas / jet up / brake down). */
export const TOUCH_MODES = ["PAD", "FULL SCREEN", "FLOAT STICK"];
/** Size and opacity of the on-screen racing buttons (A / B / JET). */
export const BTN_SIZES = [{ name: "SMALL", k: 0.8 }, { name: "NORMAL", k: 1 }, { name: "LARGE", k: 1.25 }, { name: "HUGE", k: 1.5 }];
export const BTN_ALPHAS = [{ name: "100%", k: 1 }, { name: "75%", k: 0.75 }, { name: "50%", k: 0.5 }, { name: "30%", k: 0.3 }];
/** Speed read-out in the dashboard: the numbers, or a dial with a needle. */
export const SPEEDO_MODES = ["NUMBERS", "DIAL"];
const KEY_BSIZE = "f1btnsize", KEY_BALPHA = "f1btnalpha", KEY_SPEEDO = "f1speedo";
const KEY_COLORS = "f1colors", KEY_PAL = "f1palette", KEY_RUM = "f1rumble", KEY_TOUCH = "f1touch", KEY_SKIDS = "f1skids";
const KEY_VOL: Record<VolumeKind, string> = { music: "f1vmusic", car: "f1vcar", skid: "f1vskid" };
/** Phones and tablets (the main pointer is a finger) start on the full-screen layout; a computer keeps the pad. */
const defaultTouch = () => {
  try {
    return typeof matchMedia === "function" && matchMedia("(pointer:coarse)").matches ? 1 : 0;
  } catch {
    return 0;
  }
};
const read = (k: string, max: number, dflt: number) => {
  try {
    const v = Number(localStorage.getItem(k));
    return localStorage.getItem(k) !== null && Number.isInteger(v) && v >= 0 && v < max ? v : dflt;
  } catch {
    return dflt;
  }
};
const write = (k: string, v: number) => {
  try {
    localStorage.setItem(k, String(v));
  } catch {
    /* private mode: the choice just lasts for this session */
  }
};

/** Gamepad buttons (standard mapping indices) driving each action. The defaults are the original layout; the player can reassign them. */
export type PadAction = "accel" | "brake" | "jet";
export const PAD_DEFAULT: Record<PadAction, number[]> = { accel: [0, 7], brake: [1, 6], jet: [2, 3, 5] };
const KEY_PADMAP = "f1padmap";
function readPadMap(): Record<PadAction, number[]> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY_PADMAP) ?? "null");
    const ok = (a: unknown) => Array.isArray(a) && a.length >= 1 && a.length <= 4 && a.every((n) => Number.isInteger(n) && n >= 0 && n < 32 && n !== 8 && n !== 9);
    if (raw && ok(raw.accel) && ok(raw.brake) && ok(raw.jet)) return { accel: raw.accel, brake: raw.brake, jet: raw.jet };
  } catch {
    /* none saved */
  }
  return { accel: [...PAD_DEFAULT.accel], brake: [...PAD_DEFAULT.brake], jet: [...PAD_DEFAULT.jet] };
}
export const padMap = readPadMap();
export function savePadMap() {
  try {
    localStorage.setItem(KEY_PADMAP, JSON.stringify(padMap));
  } catch {
    /* private mode: lasts for this session */
  }
}
/** One button for an action (taking it away from the other actions), or the defaults back when `btn` is null. */
export function setPadButton(action: PadAction | null, btn: number | null) {
  if (action === null || btn === null) {
    for (const a of Object.keys(PAD_DEFAULT) as PadAction[]) padMap[a] = [...PAD_DEFAULT[a]];
  } else {
    for (const a of Object.keys(PAD_DEFAULT) as PadAction[]) if (a !== action) padMap[a] = padMap[a].filter((b) => b !== btn);
    padMap[action] = [btn];
    for (const a of Object.keys(PAD_DEFAULT) as PadAction[]) if (padMap[a].length === 0) padMap[a] = PAD_DEFAULT[a].filter((b) => b !== btn); // an action left without a button gets its original ones back
  }
  savePadMap();
}
/** Names of the buttons of a standard pad. */
const PAD_NAMES = ["A", "B", "X", "Y", "LB", "RB", "LT", "RT", "SELECT", "START", "L3", "R3", "UP", "DOWN", "LEFT", "RIGHT", "HOME"];
export const padButtonName = (i: number) => PAD_NAMES[i] ?? "BTN" + i;

export const options = {
  palette: read(KEY_PAL, PALETTES.length, 0),
  rumble: read(KEY_RUM, RUMBLE_LEVELS.length, 2),
  colors: read(KEY_COLORS, 2, 1), // on unless the player switched it off. 1: sky, grass and team liveries in colour (classic green palette only)
  touch: read(KEY_TOUCH, TOUCH_MODES.length, defaultTouch()), // full screen on a phone or tablet until the player picks the pad
  btnSize: read(KEY_BSIZE, BTN_SIZES.length, 1),
  btnAlpha: read(KEY_BALPHA, BTN_ALPHAS.length, 0),
  speedo: read(KEY_SPEEDO, SPEEDO_MODES.length, 0),
  skids: read(KEY_SKIDS, 4, 1), // tyre marks left on the road: 0 off, 1 the player only, 2 the human players, 3 every car
  vol: { music: read(KEY_VOL.music, VOLUME_STEPS + 1, VOLUME_STEPS), car: read(KEY_VOL.car, VOLUME_STEPS + 1, VOLUME_STEPS), skid: read(KEY_VOL.skid, VOLUME_STEPS + 1, VOLUME_STEPS) } as Record<VolumeKind, number>,
};
/** Gain multiplier of a volume setting: squared, so that the middle of the scale sounds about half as loud. 10 = the original level. */
export const volumeGain = (k: VolumeKind) => (options.vol[k] / VOLUME_STEPS) ** 2;
/** Sets a volume (clamped, or wrapping from the top back to 0 when `wrap` is set, for taps). */
export function setVolume(k: VolumeKind, v: number, wrap = false) {
  options.vol[k] = wrap && v > VOLUME_STEPS ? 0 : Math.max(0, Math.min(VOLUME_STEPS, v));
  write(KEY_VOL[k], options.vol[k]);
}
/** 0..1.8: multiplies the pad's rumble magnitudes and durations. */
export const rumbleScale = () => RUMBLE_LEVELS[options.rumble].k;

const rgb = (c: string) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
const ORIGINAL_R = [0xe0, 0x88, 0x34, 0x08]; // red channel of the four original shades: pixels are recognised by it
let lut = new Uint8ClampedArray(256 * 3);
function buildLut() {
  const cols = PALETTES[options.palette].colors.map(rgb);
  for (let r = 0; r < 256; r++) {
    // position between two original shades (also handles blended pixels such as translucent ghost cars)
    let i = 0;
    while (i < 2 && r < ORIGINAL_R[i + 1]) i++;
    const f = Math.max(0, Math.min(1, (ORIGINAL_R[i] - r) / (ORIGINAL_R[i] - ORIGINAL_R[i + 1])));
    for (let c = 0; c < 3; c++) lut[r * 3 + c] = Math.round(cols[i][c] + (cols[i + 1][c] - cols[i][c]) * f);
  }
}
const lum = (c: string) => {
  const [r, g, b] = rgb(c);
  return 0.3 * r + 0.59 * g + 0.11 * b;
};
function applyPage() {
  if (typeof document === "undefined") return;
  const sorted = [...PALETTES[options.palette].colors].sort((a, b) => lum(a) - lum(b));
  const root = document.documentElement;
  root.style.setProperty("--bg", sorted[0]);
  root.style.setProperty("--fg", sorted[3]);
  root.style.setProperty("--g", sorted[1]);
  root.style.setProperty("--btn", `color-mix(in srgb, ${sorted[0]} 85%, ${sorted[3]})`);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", sorted[0]);
}
export function setPalette(i: number) {
  options.palette = ((i % PALETTES.length) + PALETTES.length) % PALETTES.length;
  write(KEY_PAL, options.palette);
  buildLut();
  applyPage();
}
export function setColors(i: number) {
  options.colors = ((i % 2) + 2) % 2;
  write(KEY_COLORS, options.colors);
}
/** The colour accents are drawn only on the classic palette: the other palettes recolour the frame by its shades. */
export const colorsActive = () => options.colors === 1 && options.palette === 0;
export function setTouch(i: number) {
  options.touch = ((i % TOUCH_MODES.length) + TOUCH_MODES.length) % TOUCH_MODES.length;
  write(KEY_TOUCH, options.touch);
  applyTouch();
}
/** Tells the page (CSS) which touch layout is on. */
export function applyTouch() {
  if (typeof document !== "undefined" && document.body) {
    document.body.dataset.touch = String(options.touch);
    document.body.style.setProperty("--bs", String(BTN_SIZES[options.btnSize].k));
    document.body.style.setProperty("--bo", String(BTN_ALPHAS[options.btnAlpha].k));
  }
}
export function setBtnSize(i: number) {
  options.btnSize = ((i % BTN_SIZES.length) + BTN_SIZES.length) % BTN_SIZES.length;
  write(KEY_BSIZE, options.btnSize);
  applyTouch();
}
export function setSpeedo(i: number) {
  options.speedo = ((i % SPEEDO_MODES.length) + SPEEDO_MODES.length) % SPEEDO_MODES.length;
  write(KEY_SPEEDO, options.speedo);
}
export function setSkids(i: number) {
  options.skids = ((i % 4) + 4) % 4;
  write(KEY_SKIDS, options.skids);
}
export function setBtnAlpha(i: number) {
  options.btnAlpha = ((i % BTN_ALPHAS.length) + BTN_ALPHAS.length) % BTN_ALPHAS.length;
  write(KEY_BALPHA, options.btnAlpha);
  applyTouch();
}
export function setRumble(i: number) {
  options.rumble = ((i % RUMBLE_LEVELS.length) + RUMBLE_LEVELS.length) % RUMBLE_LEVELS.length;
  write(KEY_RUM, options.rumble);
}
/** Recolours the finished frame. The game always draws with the original four shades. Call once per displayed frame. */
export function applyPalette(ctx: CanvasRenderingContext2D, w: number, h: number) {
  if (options.palette === 0) return;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i] * 3;
    d[i] = lut[r];
    d[i + 1] = lut[r + 1];
    d[i + 2] = lut[r + 2];
  }
  ctx.putImageData(img, 0, 0);
}
buildLut();
if (options.palette !== 0) applyPage();
if (typeof document !== "undefined") (document.body ? applyTouch() : addEventListener("DOMContentLoaded", applyTouch));
